import * as Dialog from "@radix-ui/react-dialog";
import { X, MapPin, Camera, FileText } from "lucide-react";
import { AssuranceBadge, PaymentMethodBadge } from "@/design/components";
import { useSeed, usePeopleIndex, useMoney, useAssurance } from "@/domain/hooks";
import type { SpendRequest } from "@/domain/types";

/**
 * The proof modal — evidence reviewed in place, inside the approvals flow (no separate
 * Evidence page). Mirrors the Philanthrify project-monitoring "proof pictures" pattern:
 * a quick, image-forward look at the photos and receipts behind an ask.
 */
export function EvidenceModal({
  ask,
  open,
  onOpenChange,
}: {
  ask: SpendRequest | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const seed = useSeed();
  const people = usePeopleIndex();
  const money = useMoney();
  const assuranceFor = useAssurance();
  if (!ask) return null;

  const evidence = (seed.evidence ?? []).filter((e) => e.linkedAskId === ask.id);
  const items = seed.spendItems.filter((i) => i.spendRequestId === ask.id);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-ink/30 backdrop-blur-[2px] animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-card border border-line bg-card shadow-overlay animate-fade-in">
          <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-base font-semibold text-ink">{ask.title}</Dialog.Title>
              <p className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-faint">
                <span className="whitespace-nowrap tabular-nums text-ink">{money(ask.amount)}</span>
                <AssuranceBadge level={assuranceFor(ask)} />
                <PaymentMethodBadge method={ask.paymentMethod} />
              </p>
            </div>
            <Dialog.Close className="focus-ring rounded-md p-1 text-faint transition-colors hover:bg-subtle hover:text-ink" aria-label="Close">
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
            <p className="mb-2 text-meta text-faint">Proof · {evidence.length} item{evidence.length === 1 ? "" : "s"}</p>
            {evidence.length === 0 ? (
              <div className="rounded-lg border border-dashed border-line bg-subtle px-4 py-8 text-center">
                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-md bg-card text-faint"><Camera className="h-5 w-5" /></div>
                <p className="text-sm font-semibold text-ink">No evidence submitted yet</p>
                <p className="mt-1 text-sm text-mute">The reporter submits photos and receipts from the field once the money is spent; they appear here for review.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {evidence.map((e) => (
                  <figure key={e.id} className="overflow-hidden rounded-lg border border-line bg-card shadow-card">
                    {e.url ? (
                      <img src={e.url} alt={e.filename ?? e.type} className="h-28 w-full object-cover" />
                    ) : (
                      <div className="flex h-28 items-center justify-center text-faint"><FileText className="h-6 w-6" /></div>
                    )}
                    <figcaption className="p-2">
                      <p className="truncate text-xs font-medium text-ink">{e.filename ?? e.note ?? e.type}</p>
                      <p className="truncate text-[11px] capitalize text-faint">
                        {e.type}{e.submittedById ? ` · ${people[e.submittedById]?.fullName ?? ""}` : ""}
                      </p>
                      {e.geo && (
                        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-faint">
                          <MapPin className="h-3 w-3" />
                          <span className="num">{e.geo.lat.toFixed(3)}, {e.geo.lng.toFixed(3)}</span>
                        </p>
                      )}
                    </figcaption>
                  </figure>
                ))}
              </div>
            )}

            {items.length > 0 && (
              <div className="mt-4">
                <p className="mb-1.5 text-meta text-faint">Itemised spend</p>
                <ul className="divide-y divide-hairline rounded-lg border border-line">
                  {items.map((i) => (
                    <li key={i.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                      <span className="min-w-0 truncate text-ink">{i.description}</span>
                      <span className="flex shrink-0 items-center gap-2">
                        {i.evidence.length > 0 && (
                          <span className="flex items-center gap-1 text-xs text-faint"><Camera className="h-3 w-3" />{i.evidence.length}</span>
                        )}
                        <span className="whitespace-nowrap tabular-nums text-ink">{money(i.amount)}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
